// Create New Order — globally-mounted flow controller (design doc §1/§10).
//
// Owns the state machine `idle → modal → picker → drawer` and every piece of
// state that lives OUTSIDE the drawer's own editing hook: the modal's field
// snapshot, the dupe-check bypass flag, the built seed/spec, staged docs +
// notes, and the flip from the 'NEW' sentinel to the real allocated pronumber.
// Mounted once in `DashboardLayout` so all three entry points (SearchResults
// header button, the Order Management nav item, LC Dashboards toolbar) share
// one `openCreateOrder()` call and one drawer instance.
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../contexts/ToastContext';
import ordersService, { type OrderDetails } from '../../../services/orders.service';
import ProDetailSlideOver, {
  CREATE_PRONUMBER_SENTINEL,
  CREATE_TEMPLATE_PRONUMBER_SENTINEL,
  type CreateOrderSpec,
  type ProDetailCreate,
} from '../../../components/pro/ProDetailSlideOver';
import CreateOrderModal from './CreateOrderModal';
import CreateOrderTemplatePicker, {
  type CreateOrderTemplatePickerPrefill,
} from './CreateOrderTemplatePicker';
import {
  buildBlankOrderSeed,
  buildCreateOrderSeed,
  type CreateOrderModalFields,
} from './buildCreateOrderSeed';
import { replayStagedDocs, type StagedDoc } from './stagedDocs';
import proDocumentsService from '../../../services/proDocuments.service';

type Step = 'idle' | 'modal' | 'picker' | 'drawer';

interface FlowState {
  step: Step;
  /** Which record this attempt creates. Templates never visit the 'picker' step —
   *  only ORDERS are created from templates (a template is never created from
   *  another template), so template mode goes straight modal -> drawer. */
  kind: 'order' | 'template';
  modalFields: CreateOrderModalFields | null;
  bypassDupeCheck: boolean;
  seed: OrderDetails | null;
  spec: CreateOrderSpec | null;
  stagedDocs: StagedDoc[];
  stagedNotes: string[];
  /** `documenttype` of the picked template's docs (they're copied by reference
   *  at create, so one of them can satisfy the Delivery-Order requirement).
   *  `[]` on the no-template path; `null` while unknown — a template whose doc
   *  list hasn't loaded (or failed to) fails the gate OPEN, since the user
   *  can't see or fix a doc that lives on the template. */
  templateDocTypes: string[] | null;
  /** Set only after the create POST commits — flips `ProDetailSlideOver`'s
   *  `pronumber` prop from the 'NEW' sentinel to the real allocated number. */
  createdPro: number | string | null;
  /** True while the picker is fetching the picked template's details — disables
   *  its rows so a double-click can't fire two seed builds. */
  pickerBusy: boolean;
}

const INITIAL_STATE: FlowState = {
  step: 'idle',
  kind: 'order',
  modalFields: null,
  bypassDupeCheck: false,
  seed: null,
  spec: null,
  stagedDocs: [],
  stagedNotes: [],
  templateDocTypes: null,
  createdPro: null,
  pickerBusy: false,
};

export interface CreateFlowCallbacks {
  /** After the create COMMITS, with the allocated pronumber. */
  onCreated?: (pronumber: string) => void;
  /** After the drawer closes and the flow resets — fires whether or not anything
   *  was created, so a host can refetch unconditionally. */
  onClosed?: () => void;
}

interface CreateOrderFlowContextValue {
  /** Full reset, then opens the modal — the ONLY path that clears a previous
   *  attempt's fields (picker "Back" and the drawer's close-guard do not). */
  openCreateOrder: () => void;
  /** The template twin. Same flow, same drawer, same staging — it just creates a
   *  template and skips the template picker.
   *
   *  The callbacks exist because this provider owns its OWN drawer (it is mounted
   *  once, globally), so a host page cannot observe the create the way it could
   *  when it rendered the create modal itself. TemplatesPage uses them to slot the
   *  new row into the grid and to refetch once the drawer closes — the template
   *  may have been approved in there, which moves it between categories. */
  openCreateTemplate: (opts?: CreateFlowCallbacks) => void;
  /** Full reset back to idle — closes whichever step is open. */
  closeFlow: () => void;
}

const CreateOrderFlowContext = createContext<CreateOrderFlowContextValue | undefined>(undefined);

export const CreateOrderFlowProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [state, setState] = useState<FlowState>(INITIAL_STATE);

  // No CSR roster and no acting-user lookup here any more: the blank seed used to
  // pre-fill the LC with whoever opened the modal (gated on that roster for V1's
  // inactive-CSR blanking), and as of 2026-10-16 it starts blank for the user to pick.
  // The drawer's own CSR select fetches the roster it needs, and the server blanks an
  // inactive TEMPLATE csr on the from-template path (order_create_service.csr_inactive).

  // `kind` is read inside handleModalContinue, which must keep a stable identity
  // (the modal takes it as a prop). A ref avoids rebuilding the callback — and
  // avoids a stale closure — without adding `state.kind` to its dep list.
  const kindRef = useRef<'order' | 'template'>('order');
  kindRef.current = state.kind;

  const openCreateOrder = useCallback(() => {
    callbacksRef.current = {};
    setState({ ...INITIAL_STATE, kind: 'order', step: 'modal' });
  }, []);

  // Held in a ref, not state: they are host-supplied functions, and re-rendering
  // the provider on every host re-render would churn the whole drawer subtree.
  const callbacksRef = useRef<CreateFlowCallbacks>({});

  const openCreateTemplate = useCallback((opts?: CreateFlowCallbacks) => {
    callbacksRef.current = opts ?? {};
    setState({ ...INITIAL_STATE, kind: 'template', step: 'modal' });
  }, []);

  const closeFlow = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  const handleModalContinue = useCallback(
    (fields: CreateOrderModalFields, opts: { bypassDupeCheck: boolean }) => {
      // A template is never created from another template, so the picker step is
      // unreachable in template mode regardless of what the modal reports.
      if (fields.useTemplate === 'Y' && kindRef.current === 'order') {
        setState((prev) => ({
          ...prev,
          modalFields: fields,
          bypassDupeCheck: opts.bypassDupeCheck,
          step: 'picker',
        }));
        return;
      }
      const seed = buildBlankOrderSeed(fields, {
        // The seed has to identify itself as a template, or every view that reads
        // `details.pronumber` renders the order branch (#33471).
        ...(kindRef.current === 'template'
          ? { pronumber: CREATE_TEMPLATE_PRONUMBER_SENTINEL }
          : {}),
      });
      const spec: CreateOrderSpec = {
        kind: kindRef.current,
        movetype: fields.movetype,
        billto: fields.billTo.recno,
        customercode: fields.customer.recno,
        bol: fields.bol,
        container: fields.container,
        templatepro: undefined,
        bypassDupeCheck: opts.bypassDupeCheck,
      };
      setState((prev) => ({
        ...prev,
        modalFields: fields,
        bypassDupeCheck: opts.bypassDupeCheck,
        seed,
        spec,
        // No template = no inherited docs, so the Delivery Order has to be
        // staged in the drawer. Known-empty, not unknown.
        templateDocTypes: [],
        step: 'drawer',
      }));
    },
    [],
  );

  const handlePickerBack = useCallback(() => {
    // Returns to the modal WITHOUT resetting its fields — the user is
    // refining the same attempt, not starting over (openCreateOrder is the
    // only reset path).
    setState((prev) => ({ ...prev, step: 'modal' }));
  }, []);

  const handlePickerPick = useCallback(
    async (templatePro: string) => {
      setState((prev) => {
        if (!prev.modalFields) return prev; // picker can't be open without modal fields
        return { ...prev, pickerBusy: true };
      });
      try {
        // The template's doc types ride along with the details fetch so the
        // Delivery-Order gate can credit a doc the create will copy by
        // reference. allSettled, not all: a failed doc list must not abort the
        // pick — it just leaves the gate open (see FlowState.templateDocTypes).
        const [detailsResult, docsResult] = await Promise.allSettled([
          ordersService.getOrderDetails(templatePro),
          proDocumentsService.list(templatePro),
        ]);
        if (detailsResult.status === 'rejected') throw detailsResult.reason;
        const { data } = detailsResult.value;
        const templateDocTypes =
          docsResult.status === 'fulfilled'
            ? docsResult.value
                .filter((d) => d.deleted !== 'Y')
                .map((d) => d.documenttype ?? '')
            : null;
        setState((prev) => {
          if (!prev.modalFields) return { ...prev, pickerBusy: false };
          const seed = buildCreateOrderSeed(data, prev.modalFields);
          const spec: CreateOrderSpec = {
            // Reached only from the picker, which is order-only.
            kind: 'order',
            movetype: prev.modalFields.movetype,
            billto: prev.modalFields.billTo.recno,
            customercode: prev.modalFields.customer.recno,
            bol: prev.modalFields.bol,
            container: prev.modalFields.container,
            templatepro: templatePro,
            bypassDupeCheck: prev.bypassDupeCheck,
          };
          return { ...prev, seed, spec, templateDocTypes, step: 'drawer', pickerBusy: false };
        });
      } catch (err: unknown) {
        const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
        const message =
          typeof detail === 'string' && detail
            ? detail
            : err instanceof Error
              ? err.message
              : `Couldn't load template ${templatePro}.`;
        showToast({ type: 'error', message });
        setState((prev) => ({ ...prev, pickerBusy: false }));
      }
    },
    [showToast],
  );

  const handleStagedDocsChange = useCallback((next: StagedDoc[]) => {
    setState((prev) => ({ ...prev, stagedDocs: next }));
  }, []);

  const handleStagedNotesChange = useCallback((next: string[]) => {
    setState((prev) => ({ ...prev, stagedNotes: next }));
  }, []);

  // Runs after the create POST commits (ProDetailContainer awaits this behind
  // a locked "Uploading documents…" footer). Replays staged doc uploads
  // against the real pronumber, then flips `createdPro` — the SlideOver's
  // `pronumber` prop becomes the real number and every create-mode branch
  // downstream falls back to the ordinary open-pro path on its own. Never
  // throws: a failed upload never rolls back the already-created order.
  const handleCreated = useCallback(
    async (pronumber: number | string) => {
      const noun = state.kind === 'template' ? 'Template' : 'Order';
      const { failed } = await replayStagedDocs(pronumber, state.stagedDocs);
      if (failed.length > 0) {
        showToast({
          type: 'warning',
          message: `${noun} ${pronumber} created, but ${failed.length} document${failed.length === 1 ? '' : 's'} failed to upload (${failed
            .map((f) => f.name)
            .join(', ')}) — re-upload from the Docs tab.`,
          duration: 8000,
        });
      }
      setState((prev) => ({ ...prev, createdPro: pronumber }));
      callbacksRef.current.onCreated?.(String(pronumber));

      // Hand the new pro off to Search Results — the same Custom Search deep
      // link the dupe flow uses (OrderDupeActions: newline-joined `q` +
      // searchBy=pronumber), and the port of V1's post-create customsearch
      // redirect. Without it the user is left standing on whatever launched the
      // flow — for the Order Management nav item that's
      // /order-management/create-order, a placeholder page with no trace of what
      // they just created, so "Auto Close On Save" drops them somewhere the new
      // pro cannot be reopened.
      //
      // The drawer SURVIVES this: the provider is mounted above <Routes> in
      // DashboardLayout, so only the page behind it swaps. Whenever the drawer
      // closes — by hand or by Auto Close On Save — the pro is already on screen.
      //
      // ORDERS only. A template create is hosted by TemplatesPage, which passes
      // onCreated/onClosed to slot the row in and refetch; navigating would
      // unmount it mid-flow, and T-pros belong on the Templates grid anyway.
      if (state.kind !== 'template') {
        navigate(
          `/searchresults?${new URLSearchParams({
            q: String(pronumber),
            searchBy: 'pronumber',
          }).toString()}`,
        );
      }
    },
    [state.kind, state.stagedDocs, showToast, navigate],
  );

  // The drawer's own unsaved-changes guard already ran before this fires
  // (ProDetailSlideOver's guardedClose) — this is a full reset, not a second
  // discard prompt.
  const handleDrawerClose = useCallback(() => {
    setState(INITIAL_STATE);
    const { onClosed } = callbacksRef.current;
    callbacksRef.current = {};
    onClosed?.();
  }, []);

  const contextValue = useMemo<CreateOrderFlowContextValue>(
    () => ({ openCreateOrder, openCreateTemplate, closeFlow }),
    [openCreateOrder, openCreateTemplate, closeFlow],
  );

  const pickerPrefill: CreateOrderTemplatePickerPrefill = useMemo(
    () => ({
      billTo: state.modalFields?.billTo ?? { recno: 0, name: '' },
      customer: state.modalFields?.customer ?? { recno: 0, name: '' },
      movetype: state.modalFields?.movetype ?? '',
      delivertoName: state.modalFields?.delivertofilter ?? '',
    }),
    [state.modalFields],
  );

  // `null` only before a seed/spec exists (modal/picker steps) — once the
  // drawer opens this stays referentially the SAME object across the
  // create→created flip (only its `stagedDocs`/`stagedNotes` contents change
  // via the setters above), per the drawer-stack contract.
  const createProp: ProDetailCreate | null = useMemo(() => {
    if (!state.seed || !state.spec) return null;
    return {
      seed: state.seed,
      spec: state.spec,
      stagedDocs: state.stagedDocs,
      onStagedDocsChange: handleStagedDocsChange,
      stagedNotes: state.stagedNotes,
      onStagedNotesChange: handleStagedNotesChange,
      templateDocTypes: state.templateDocTypes,
      onCreated: handleCreated,
    };
  }, [
    state.seed,
    state.spec,
    state.stagedDocs,
    state.stagedNotes,
    state.templateDocTypes,
    handleStagedDocsChange,
    handleStagedNotesChange,
    handleCreated,
  ]);

  return (
    <CreateOrderFlowContext.Provider value={contextValue}>
      {children}
      <CreateOrderModal
        open={state.step === 'modal'}
        kind={state.kind}
        onClose={closeFlow}
        onContinue={handleModalContinue}
      />
      <CreateOrderTemplatePicker
        open={state.step === 'picker'}
        busy={state.pickerBusy}
        prefill={pickerPrefill}
        onPick={handlePickerPick}
        onBack={handlePickerBack}
        onClose={closeFlow}
      />
      <ProDetailSlideOver
        pronumber={
          state.createdPro
          ?? (state.kind === 'template'
            ? CREATE_TEMPLATE_PRONUMBER_SENTINEL
            : CREATE_PRONUMBER_SENTINEL)
        }
        isOpen={state.step === 'drawer'}
        onClose={handleDrawerClose}
        create={createProp}
      />
    </CreateOrderFlowContext.Provider>
  );
};

export const useCreateOrderFlow = (): CreateOrderFlowContextValue => {
  const ctx = useContext(CreateOrderFlowContext);
  if (!ctx) throw new Error('useCreateOrderFlow must be used inside CreateOrderFlowProvider');
  return ctx;
};
